/**
 * @module AuthStore
 *
 * This module contains the implementation of the authentication store using NgRx Signals.
 * It manages the authentication state, including user data, authentication status,
 * and related methods for login, logout, and loading authentication state.
 *
 * Key components:
 * - `withState`: Provides initial authentication state.
 * - `withComputed`: Creates computed properties based on authentication state.
 * - `withMethods`: Defines methods for handling login, logout, and loading authentication state.
 * - `withHooks`: Registers lifecycle hooks, specifically on store initialization.
 *
 */

import { AuthService } from '../services/auth.service';
import { computed, inject, InjectionToken } from '@angular/core';
import { loadAuthState, login, logout } from './auth.methods';
import {
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AuthState, Role, UserDistributor } from '../models/auth.models';
import { initialState } from './auth.constants';
import { LocalStorageStore } from '@localStore';

// InjectionToken for providing the initial state of the authentication store
const AUTH_STATE = new InjectionToken<AuthState>('AuthState', {
  factory: () => initialState, // Fallback to initialState if no value is provided
});

// Definition of the AuthStore using NgRx Signals
export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(() => inject(AUTH_STATE)),
  withComputed(({ isAuthenticated, user }) => ({
    // Computed properties for user-related information
    isLoggedIn: computed<boolean>(() => isAuthenticated()), // Checks if the user is authenticated
    userName: computed<string>(() => user()?.name || ''),
    userNickname: computed<string>(() => user()?.nickname || ''),
    roles: computed<Role[]>(() => user()?.['http://sercide.com/roles'] || []),
    tenants: computed<UserDistributor[]>(() => user()?.['http://sercide.com/distributors'] || []),
  })),
  withMethods((store, authService = inject(AuthService), localStorageStore = inject(LocalStorageStore)) => ({
    // Methods to handle authentication actions
    login: (target: string) => login(store, authService, target),
    logout: () => logout(store, authService),
    loadAuthState: loadAuthState(store, authService),
  })),
  withHooks({
    // Lifecycle hook to load authentication state on store initialization
    onInit(store) {
      // Automatically load authentication state when the store is initialized
      store.loadAuthState();
    },
  })
);
